import { useMemo, useEffect } from "react";
import { Bucket, PendingOrder } from "../../sdk/types/data";
import { usePendingOrders } from "../../hooks/usePendingOrders";
import { useOnboard } from "../../hooks/useOnboard";
import { DEFAULT_BASE_URL } from "../../constants";
import ListViewMac, { ListViewMacColumn } from "../../components/ListViewMac";

interface CancelOpensTabProps {
  buckets: Bucket[];
  selectedMarketId: string | number | null;
}

export default function CancelOpensTab({
  buckets,
  selectedMarketId,
}: CancelOpensTabProps) {
  // Get wallet address and auth token from onboard hook
  const { walletAddress, authToken } = useOnboard(DEFAULT_BASE_URL);

  // Load pending orders (uses context when inside SubscriptionProvider)
  const {
    orders,
    loading: ordersLoading,
    error: ordersError,
    refresh: refreshOrders,
  } = usePendingOrders({
    baseURL: DEFAULT_BASE_URL,
    authToken,
    walletAddress,
    useWebSocket: true,
    refreshInterval: 0,
    marketId: selectedMarketId ? String(selectedMarketId) : undefined,
  });

  // Refresh orders when the tab becomes active (component mounts) or when market ID changes
  useEffect(() => {
    refreshOrders();
  }, [refreshOrders, selectedMarketId]);

  // Filter to only show open orders (OPEN, PENDING, PARTIAL statuses)
  const openOrders = useMemo(() => {
    return orders.filter((order) => {
      const status = order.status.toUpperCase();
      return (
        status === "OPEN" ||
        status === "PENDING" ||
        status === "PARTIAL"
      );
    });
  }, [orders]);

  // Define columns for ListViewMac
  const columns: ListViewMacColumn<PendingOrder>[] = useMemo(
    () => [
      {
        key: "orderId",
        label: "Order ID",
        width: 200,
        render: (order) => (
          <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
            {order.orderId}
          </span>
        ),
      },
      {
        key: "side",
        label: "Side",
        width: 80,
        align: "center",
        render: (order) => (
          <span
            style={{
              color: order.side === "BUY" || order.side === "LONG" ? "#4CAF50" : "#F44336",
              fontWeight: "bold",
            }}
          >
            {order.side}
          </span>
        ),
      },
      {
        key: "price",
        label: "Entry Price",
        width: 120,
        align: "right",
        render: (order) => (
          <span>{order.price.toFixed(2)}</span>
        ),
      },
      {
        key: "filledQuantity",
        label: "Filled Size",
        width: 120,
        align: "right",
        render: (order) => (
          <span>{order.filledQuantity.toFixed(4)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Open Orders List */}
      <div style={{ marginTop: "20px" }}>
        {ordersLoading ? (
          <div className="hooks-test-loading-state">
            Loading orders...
          </div>
        ) : ordersError ? (
          <div className="hooks-test-loading-state" style={{ color: "#F44336" }}>
            Error: {ordersError}
          </div>
        ) : openOrders.length === 0 ? (
          <div className="hooks-test-loading-state">
            No open orders found
          </div>
        ) : (
          <ListViewMac
            data={openOrders}
            columns={columns}
            maxItems={10}
            emptyMessage="No open orders"
            rowHeight={24}
            showHeader={true}
          />
        )}
      </div>

      <div style={{ marginTop: "20px" }}>
        <button
          className="hooks-test-button"
          disabled={!selectedMarketId || buckets.length === 0 || openOrders.length === 0}
          style={{
            width: "100%",
            padding: "20px",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          Cancel Open Orders
        </button>
      </div>
    </div>
  );
}
